import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// Gantt is a SCHEDULE view — uses schedule_sections (work breakdown),
// not the SOV's sections table (cost breakdown). Different real-world
// concepts; see project_summit_polish_2026_05_21 memory.
import { getGanttData, updateTask, getScheduleSections, getDependencyAlerts } from '../api';
import LoadingState from './LoadingState';
import { useTheme } from '../hooks/useTheme';
import { useProjectToday, nowOn } from '../lib/projectToday';
import TestDateControl from './TestDateControl';
import { Icon } from './ui';
import { fmtRange } from './subs/SubMovesBanner';
import { saidOf } from './subs/SubProgressBanner';
import { rollUp } from '../lib/rollup';
import { milestoneColor } from '../lib/milestoneColor';

const getSectionPrefix = (section) => {
  if (!section || section === 'Unassigned') return 'T';
  const words = section.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase();
};

const generateTaskCode = (section, index) => {
  const prefix = getSectionPrefix(section);
  return `${prefix}-${String(index).padStart(3, '0')}`;
};

// Exported with ganttTimeHeaders and GANTT_ZOOM_STYLES for the project Subcontractors tab's sub schedule
// (SubGantt): the same Day / Week / Month / Quarter, header and toolbar as the Schedule tab (Duncan 2026-10-23).
export const ZOOM_LEVELS = [
  { key: 'day', label: 'Day', dayWidth: 36 },
  { key: 'week', label: 'Week', dayWidth: 16 },
  { key: 'month', label: 'Month', dayWidth: 5 },
  { key: 'quarter', label: 'Quarter', dayWidth: 1.8 },
];

/** The Gantt's two time-header rows for a zoom -- primary (months / quarters / years) over secondary (days / weeks /
    months / quarters) -- as { label, left, width } in px from rangeStart. */
export function ganttTimeHeaders(rangeStart, effectiveRangeEnd, effectiveTotalDays, zoom) {
  if (!rangeStart || !effectiveRangeEnd) return { primary: [], secondary: [] };

  const eDays = effectiveTotalDays;
  const eEnd = effectiveRangeEnd;
  const primary = [];
  const secondary = [];

  if (zoom.key === 'day' || zoom.key === 'week') {
    // Primary: months, Secondary: weeks or days
    const cur = new Date(rangeStart);
    cur.setDate(1);
    if (cur < rangeStart) cur.setMonth(cur.getMonth());

    while (cur <= eEnd) {
      const dayOff = Math.round((cur - rangeStart) / (1000 * 60 * 60 * 24));
      const nextMonth = new Date(cur);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const endOff = Math.round((nextMonth - rangeStart) / (1000 * 60 * 60 * 24));
      primary.push({
        label: cur.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        left: Math.max(0, dayOff) * zoom.dayWidth,
        width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
      });
      cur.setMonth(cur.getMonth() + 1);
    }

    if (zoom.key === 'day') {
      // Show individual days
      for (let i = 0; i < eDays; i++) {
        const d = new Date(rangeStart);
        d.setDate(d.getDate() + i);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        secondary.push({
          label: d.getDate().toString(),
          left: i * zoom.dayWidth,
          width: zoom.dayWidth,
          isWeekend,
        });
      }
    } else {
      // Show week starts
      const d = new Date(rangeStart);
      // Move to Monday
      const dayOfWeek = d.getDay();
      const toMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
      d.setDate(d.getDate() + toMonday);
      while (d <= eEnd) {
        const dayOff = Math.round((d - rangeStart) / (1000 * 60 * 60 * 24));
        secondary.push({
          label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          left: dayOff * zoom.dayWidth,
          width: 7 * zoom.dayWidth,
        });
        d.setDate(d.getDate() + 7);
      }
    }
  } else if (zoom.key === 'month') {
    // Primary: quarters, Secondary: months
    const cur = new Date(rangeStart);
    cur.setDate(1);
    const quarter = Math.floor(cur.getMonth() / 3);
    cur.setMonth(quarter * 3);

    while (cur <= eEnd) {
      const dayOff = Math.round((cur - rangeStart) / (1000 * 60 * 60 * 24));
      const nextQ = new Date(cur);
      nextQ.setMonth(nextQ.getMonth() + 3);
      const endOff = Math.round((nextQ - rangeStart) / (1000 * 60 * 60 * 24));
      const q = Math.floor(cur.getMonth() / 3) + 1;
      primary.push({
        label: `Q${q} ${cur.getFullYear()}`,
        left: Math.max(0, dayOff) * zoom.dayWidth,
        width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
      });
      cur.setMonth(cur.getMonth() + 3);
    }

    const m = new Date(rangeStart);
    m.setDate(1);
    while (m <= eEnd) {
      const dayOff = Math.round((m - rangeStart) / (1000 * 60 * 60 * 24));
      const nextM = new Date(m);
      nextM.setMonth(nextM.getMonth() + 1);
      const endOff = Math.round((nextM - rangeStart) / (1000 * 60 * 60 * 24));
      secondary.push({
        label: m.toLocaleDateString('en-US', { month: 'short' }),
        left: Math.max(0, dayOff) * zoom.dayWidth,
        width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
      });
      m.setMonth(m.getMonth() + 1);
    }
  } else {
    // Quarter view: Primary: years, Secondary: quarters
    const cur = new Date(rangeStart);
    cur.setMonth(0, 1);
    while (cur <= eEnd) {
      const dayOff = Math.round((cur - rangeStart) / (1000 * 60 * 60 * 24));
      const nextY = new Date(cur);
      nextY.setFullYear(nextY.getFullYear() + 1);
      const endOff = Math.round((nextY - rangeStart) / (1000 * 60 * 60 * 24));
      primary.push({
        label: cur.getFullYear().toString(),
        left: Math.max(0, dayOff) * zoom.dayWidth,
        width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
      });
      cur.setFullYear(cur.getFullYear() + 1);
    }

    const q = new Date(rangeStart);
    q.setDate(1);
    const qStart = Math.floor(q.getMonth() / 3);
    q.setMonth(qStart * 3);
    while (q <= eEnd) {
      const dayOff = Math.round((q - rangeStart) / (1000 * 60 * 60 * 24));
      const nextQ = new Date(q);
      nextQ.setMonth(nextQ.getMonth() + 3);
      const endOff = Math.round((nextQ - rangeStart) / (1000 * 60 * 60 * 24));
      const qNum = Math.floor(q.getMonth() / 3) + 1;
      secondary.push({
        label: `Q${qNum}`,
        left: Math.max(0, dayOff) * zoom.dayWidth,
        width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
      });
      q.setMonth(q.getMonth() + 3);
    }
  }

  return { primary, secondary };
}

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 56; // 24 months row + 32 weeks row, per spec
const LABEL_WIDTH = 280;

const STATUS_COLORS = {
  complete: { bar: '#16a34a', bg: '#16a34a', text: '#fff' },
  completed: { bar: '#16a34a', bg: '#16a34a', text: '#fff' },
  'in-progress': { bar: '#2563eb', bg: '#93c5fd', text: '#fff' },
  'in progress': { bar: '#2563eb', bg: '#93c5fd', text: '#fff' },
  'not-started': { bar: '#9ca3af', bg: '#e5e7eb', text: '#fff' },
  'not started': { bar: '#9ca3af', bg: '#e5e7eb', text: '#fff' },
};

/**
 * Stable hash → OKLCH hue for a section name. Used as the per-section
 * roll-up bar fill and the section header's leading dot. Doesn't
 * require a schema change — the same section name always produces the
 * same color across reloads + dark/light. Simple string-walking hash
 * keeps the distribution decent without pulling in a hash lib.
 */
const sectionHue = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % 360;
};
const sectionColor = (name) => `oklch(0.55 0.1 ${sectionHue(name)})`;

/**
 * Schedule-redesign: map task.task_type → CSS variable for the
 * task-type accent color. Drives the soft-fill Gantt bars (12% mix bg
 * + 30% mix border + 0.85 progress overlay) and the milestone diamond.
 * Mirrors the helper in TasksTab.js — kept in lockstep manually until
 * we have a reason to extract it.
 */
// Exported for the project Subcontractors tab's sub schedule (SubGantt), so its bars match.
export const getTaskTypeColor = (task) => {
  // A milestone with its own color (lib/milestoneColor.js, 2026-11-02): its diamond here and on the Subcontractors tab.
  const own = milestoneColor(task, null);
  if (own) return own;
  const tt = (task?.task_type || task?.type || '').toLowerCase();
  switch (tt) {
    case 'material':
    case 'procurement':
      return 'var(--type-procurement)';
    case 'labor':
    case 'work':
      return 'var(--type-labor)';
    case 'engineering':
      return 'var(--type-engineering)';
    case 'inspection':
      return 'var(--type-inspection)';
    case 'documents':
      return 'var(--type-documents)';
    case 'milestone':
      return 'var(--type-milestone)';
    default:
      return 'var(--text-3)';
  }
};

// Determine color based on actual progress, not just status field
const getProgressColors = (task) => {
  const progress = task.progress || 0;
  const status = task.status?.toLowerCase() || 'not started';
  if (progress >= 100 || status === 'complete' || status === 'completed') {
    return STATUS_COLORS['complete'];
  }
  if (progress > 0) {
    return STATUS_COLORS['in-progress'];
  }
  return STATUS_COLORS['not started'];
};

const getStatusColors = (status) => {
  return STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS['not started'];
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysBetween = (d1, d2) => {
  const a = new Date(d1 + 'T00:00:00');
  const b = new Date(d2 + 'T00:00:00');
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

const GanttTab = ({ projectId, refreshSummary, onTaskOpen, refreshKey, inSplitView = false, sharedSchedule }) => {
  const { testDate } = useProjectToday(); // a test job's test date is the today line (2026-10-20)
  const t = useTheme();
  const styles = getStyles(t);
  const [_localTasks, _setLocalTasks] = useState([]);
  const tasks = sharedSchedule ? sharedSchedule.ganttTasks : _localTasks;
  // In shared mode, ignore Gantt's optimistic local setTasks calls but queue a
  // refresh of the parent's task list so mutations made here (drag-reorder etc.)
  // propagate back to both panels once the backend PUTs settle.
  const sharedRefreshTimerRef = useRef(null);
  const setTasks = sharedSchedule
    ? () => {
        if (sharedRefreshTimerRef.current) clearTimeout(sharedRefreshTimerRef.current);
        sharedRefreshTimerRef.current = setTimeout(() => sharedSchedule.refreshTasks(), 600);
      }
    : _setLocalTasks;
  const [_localLoading, _setLocalLoading] = useState(true);
  const loading = sharedSchedule ? sharedSchedule.loading : _localLoading;
  const setLoading = sharedSchedule ? () => {} : _setLocalLoading;
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1); // default 'week'
  const [_localExpandedSections, _setLocalExpandedSections] = useState({});
  // When shared, derive expandedSections from collapsedSections (opposite polarity).
  const expandedSections = sharedSchedule
    ? Object.fromEntries(Object.entries(sharedSchedule.collapsedSections).map(([k, v]) => [k, !v]))
    : _localExpandedSections;
  const setExpandedSections = sharedSchedule ? () => {} : _setLocalExpandedSections;
  const [tooltip, setTooltip] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverTask, setDragOverTask] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [_localSavedSections, _setLocalSavedSections] = useState([]);
  const savedSections = sharedSchedule ? sharedSchedule.savedSections : _localSavedSections;
  const setSavedSections = sharedSchedule ? () => {} : _setLocalSavedSections;
  const [_localDepAlerts, _setLocalDepAlerts] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  const depAlerts = sharedSchedule ? sharedSchedule.depAlerts : _localDepAlerts;
  const setDepAlerts = sharedSchedule ? () => {} : _setLocalDepAlerts;
  const [labelWidth, setLabelWidth] = useState(inSplitView ? 0 : LABEL_WIDTH);
  const chartAreaRef = useRef(null);
  const svgRef = useRef(null);
  const labelColumnRef = useRef(null);
  const timeHeaderScrollRef = useRef(null);
  // Sticky-bottom horizontal scrollbar: a thin separate element kept at
  // the bottom edge of the viewport so users without a horizontal scroll
  // wheel / trackpad can still grab + drag to pan the chart. Two-way
  // sync with chartAreaRef.
  const bottomScrollbarRef = useRef(null);
  // Flag to break the sync feedback loop — when we programmatically set
  // scrollLeft on one element to match the other, the resulting scroll
  // event would otherwise fire back and re-set the source.
  const isSyncingScrollRef = useRef(false);
  const resizingRef = useRef(false);
  const resizeStartRef = useRef({ x: 0, width: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingRef.current) return;
      const diff = e.clientX - resizeStartRef.current.x;
      const newWidth = Math.max(150, Math.min(600, resizeStartRef.current.width + diff));
      setLabelWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        resizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    resizeStartRef.current = { x: e.clientX, width: labelWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (sharedSchedule) {
      // Parent owns data; loading state is read from sharedSchedule.loading.
      initialLoadDoneRef.current = true;
      return;
    }
    const fetchData = async () => {
      try {
        // Only show loading spinner on initial load, not on refreshes (which reset scroll position)
        if (!initialLoadDoneRef.current) setLoading(true);
        const [data, secs, depAlertData] = await Promise.all([
          getGanttData(projectId),
          getScheduleSections(projectId),
          getDependencyAlerts(projectId).catch(() => ({ alerts: [], flagged_task_ids: [], summary: {} })),
        ]);
        setTasks(data.tasks || []);
        setSavedSections(secs || []);
        setDepAlerts(depAlertData);
        setError(null);
        initialLoadDoneRef.current = true;
      } catch (err) {
        setError(err.message || 'Failed to load Gantt data');
      } finally {
        setLoading(false);
      }
    };
    if (projectId) fetchData();
  }, [projectId, refreshKey]);

  // Compute date range with padding
  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    if (!tasks.length) return { rangeStart: null, rangeEnd: null, totalDays: 0 };
    const dates = tasks.flatMap(t => {
      const d = [];
      if (t.start_date) d.push(new Date(t.start_date + 'T00:00:00'));
      if (t.end_date) d.push(new Date(t.end_date + 'T00:00:00'));
      return d;
    }).filter(d => !isNaN(d));
    if (!dates.length) return { rangeStart: null, rangeEnd: null, totalDays: 0 };

    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    // Add 14 days padding each side
    min.setDate(min.getDate() - 14);
    max.setDate(max.getDate() + 14);
    const total = Math.round((max - min) / (1000 * 60 * 60 * 24)) + 1;
    return { rangeStart: min, rangeEnd: max, totalDays: total };
  }, [tasks]);

  // Group by building_area_phase. When sharedSchedule is provided, prefer its
  // precomputed groupedGanttTasks/sections so this panel renders the same rows
  // (in the same order) as TasksTab.
  const _localGroupedTasks = useMemo(() => {
    const groups = {};
    tasks.forEach(task => {
      const section = task.building_area_phase || 'Unsectioned';
      if (!groups[section]) groups[section] = [];
      groups[section].push(task);
    });
    Object.keys(groups).forEach(section => {
      groups[section].sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
    });
    return groups;
  }, [tasks]);
  const groupedTasks = sharedSchedule ? sharedSchedule.groupedGanttTasks : _localGroupedTasks;

  const _localSections = useMemo(() => {
    const savedNames = savedSections.map(s => s.section_name);
    const savedSet = new Set(savedNames);
    const taskSections = Object.keys(_localGroupedTasks);
    const unsaved = taskSections.filter(n => !savedSet.has(n)).sort();
    return [...savedNames.filter(n => taskSections.includes(n) || savedSet.has(n)), ...unsaved];
  }, [_localGroupedTasks, savedSections]);
  const sections = sharedSchedule ? sharedSchedule.sections : _localSections;

  // Set of task IDs with dependency alerts for quick lookup
  const flaggedTaskSet = useMemo(() => new Set(depAlerts.flagged_task_ids || []), [depAlerts]);

  // Alert messages indexed by task ID for tooltips
  const alertsByTaskId = useMemo(() => {
    const map = {};
    for (const alert of (depAlerts.alerts || [])) {
      if (!map[alert.task_id]) map[alert.task_id] = [];
      map[alert.task_id].push(alert);
      if (alert.related_task_id) {
        if (!map[alert.related_task_id]) map[alert.related_task_id] = [];
        // Avoid duplicates — only add if not already referencing this alert
        if (!map[alert.related_task_id].some(a => a.message === alert.message)) {
          map[alert.related_task_id].push(alert);
        }
      }
    }
    return map;
  }, [depAlerts]);

  // Set of violated dependency pairs for coloring arrows red: "taskId-predId"
  const violatedDepPairs = useMemo(() => {
    const pairs = new Set();
    for (const alert of (depAlerts.alerts || [])) {
      if (alert.type === 'date_violation' && alert.task_id && alert.related_task_id) {
        pairs.add(`${alert.task_id}-${alert.related_task_id}`);
      }
    }
    return pairs;
  }, [depAlerts]);

  // Build a flat list of visible rows. Prefer the parent's precomputed list
  // when available so this panel's rows match TasksTab 1:1.
  const _localVisibleRows = useMemo(() => {
    const rows = [];
    sections.forEach(section => {
      rows.push({ type: 'section', section });
      if (expandedSections[section] !== false) {
        (groupedTasks[section] || []).forEach(task => {
          rows.push({ type: 'task', task, section });
        });
      }
    });
    return rows;
  }, [sections, groupedTasks, expandedSections]);
  const visibleRows = sharedSchedule ? sharedSchedule.visibleRows : _localVisibleRows;

  // Task ID -> row index map (for arrow endpoints)
  const taskRowIndex = useMemo(() => {
    const map = {};
    visibleRows.forEach((row, idx) => {
      if (row.type === 'task') map[row.task.id] = idx;
    });
    return map;
  }, [visibleRows]);

  const zoom = ZOOM_LEVELS[zoomLevel];
  // Ensure chart is wide enough to fill the viewport (estimate ~1800px max screen)
  const minChartPx = 1800;
  const minDaysForViewport = Math.ceil(minChartPx / zoom.dayWidth);
  const effectiveTotalDays = Math.max(totalDays, minDaysForViewport);
  const chartWidth = effectiveTotalDays * zoom.dayWidth;
  // Extended range end for time headers/grid lines to fill the extra space
  const effectiveRangeEnd = useMemo(() => {
    if (!rangeStart) return rangeEnd;
    const d = new Date(rangeStart);
    d.setDate(d.getDate() + effectiveTotalDays);
    return d;
  }, [rangeStart, rangeEnd, effectiveTotalDays]);

  const getDayOffset = useCallback((dateStr) => {
    if (!rangeStart || !dateStr) return 0;
    const d = new Date(dateStr + 'T00:00:00');
    return Math.round((d - rangeStart) / (1000 * 60 * 60 * 24));
  }, [rangeStart]);

  const getBarX = useCallback((dateStr) => {
    return getDayOffset(dateStr) * zoom.dayWidth;
  }, [getDayOffset, zoom.dayWidth]);

  const getBarWidth = useCallback((startDate, endDate) => {
    const days = daysBetween(startDate, endDate) + 1;
    return Math.max(days * zoom.dayWidth, 4);
  }, [zoom.dayWidth]);

  // Today line position
  const todayOffset = useMemo(() => {
    if (!rangeStart) return null;
    const today = nowOn(testDate);
    today.setHours(0, 0, 0, 0);
    const dayOff = Math.round((today - rangeStart) / (1000 * 60 * 60 * 24));
    if (dayOff < 0 || dayOff > effectiveTotalDays) return null;
    return dayOff * zoom.dayWidth;
  }, [rangeStart, effectiveTotalDays, zoom.dayWidth, testDate]);

  // Generate time header markers
  const timeHeaders = useMemo(
    () => ganttTimeHeaders(rangeStart, effectiveRangeEnd, effectiveTotalDays, zoom),
    [rangeStart, effectiveRangeEnd, effectiveTotalDays, zoom],
  );

  // Dependency arrows
  const arrows = useMemo(() => {
    const result = [];
    visibleRows.forEach((row) => {
      if (row.type !== 'task' || !row.task.dependencies?.length) return;
      const task = row.task;
      const toIdx = taskRowIndex[task.id];
      if (toIdx === undefined) return;

      task.dependencies.forEach(dep => {
        const fromIdx = taskRowIndex[dep.predecessor_task_id];
        if (fromIdx === undefined) return;
        const predRow = visibleRows[fromIdx];
        if (!predRow || predRow.type !== 'task') return;
        const pred = predRow.task;

        const depType = dep.dependency_type || 'FS';
        let startX, startY, endX, endY;

        const predBarX = getBarX(pred.start_date);
        const predBarEnd = predBarX + getBarWidth(pred.start_date, pred.end_date);
        const taskBarX = getBarX(task.start_date);
        const taskBarEnd = taskBarX + getBarWidth(task.start_date, task.end_date);

        const rowCenterY = (idx) => idx * ROW_HEIGHT + ROW_HEIGHT / 2;

        if (depType === 'FS') {
          startX = predBarEnd;
          endX = taskBarX;
        } else if (depType === 'SS') {
          startX = predBarX;
          endX = taskBarX;
        } else if (depType === 'FF') {
          startX = predBarEnd;
          endX = taskBarEnd;
        } else if (depType === 'SF') {
          startX = predBarX;
          endX = taskBarEnd;
        } else {
          startX = predBarEnd;
          endX = taskBarX;
        }

        startY = rowCenterY(fromIdx);
        endY = rowCenterY(toIdx);

        result.push({ startX, startY, endX, endY, depType, taskId: task.id, predId: dep.predecessor_task_id });
      });
    });
    return result;
  }, [visibleRows, taskRowIndex, getBarX, getBarWidth]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: prev[section] === false ? true : false }));
  };

  const scrollToToday = () => {
    if (chartAreaRef.current && todayOffset !== null) {
      chartAreaRef.current.scrollLeft = todayOffset - chartAreaRef.current.clientWidth / 2;
    }
  };

  // Auto-scroll to today on initial load only (not on data refreshes)
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (!loading && todayOffset !== null && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      setTimeout(scrollToToday, 100);
    }
  }, [loading, todayOffset]);

  // Also scroll when zoom level changes
  useEffect(() => {
    if (!loading && todayOffset !== null && hasScrolledRef.current) {
      setTimeout(scrollToToday, 100);
    }
  }, [zoomLevel]);

  // ============ DRAG AND DROP ============
  const handleDragStart = (e, task, section) => {
    setDraggedTask(task);
    setDragSection(section);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTask(null);
    setDragOverTask(null);
    setDragSection(null);
  };

  const handleDragOver = (e, task) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (task.id !== dragOverTask) {
      setDragOverTask(task.id);
    }
  };

  const handleDrop = async (e, targetTask, section) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Only allow reorder within same section -- and not with subtasks: they stay in date order under their task, which the
    // Schedule's list moves as a block (TasksTab).
    if (dragSection !== section || draggedTask._depth || draggedTask._kids || targetTask._depth || targetTask._addFor) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    const sectionTasks = [...(groupedTasks[section] || [])];
    const dragIdx = sectionTasks.findIndex(t => t.id === draggedTask.id);
    const dropIdx = sectionTasks.findIndex(t => t.id === targetTask.id);

    if (dragIdx === -1 || dropIdx === -1) return;

    // Reorder: remove dragged, insert at drop position
    const [moved] = sectionTasks.splice(dragIdx, 1);
    sectionTasks.splice(dropIdx, 0, moved);

    // Build new codes for the reordered section
    const updates = sectionTasks.map((task, idx) => ({
      id: task.id,
      newCode: generateTaskCode(section, idx + 1)
    }));

    // Optimistic UI update
    const otherTasks = tasks.filter(t => (t.building_area_phase || 'Unassigned') !== section);
    const reorderedSectionTasks = sectionTasks.map((t, idx) => ({
      ...t,
      code: updates[idx].newCode
    }));
    setTasks([...otherTasks, ...reorderedSectionTasks]);

    setDraggedTask(null);
    setDragOverTask(null);

    // Persist to backend
    for (const upd of updates) {
      try {
        await updateTask(upd.id, { task_code: upd.newCode });
      } catch (err) {
        console.error('Error updating task code:', err);
      }
    }
  };

  // Sync horizontal scroll from the chart area outward — to the sticky
  // time header at the top AND to the sticky bottom scrollbar. Guarded
  // by isSyncingScrollRef so the cross-sync from the bottom scrollbar
  // doesn't echo back here in a loop.
  const handleChartScroll = useCallback(() => {
    if (isSyncingScrollRef.current) return;
    if (!chartAreaRef.current) return;
    isSyncingScrollRef.current = true;
    const sl = chartAreaRef.current.scrollLeft;
    if (timeHeaderScrollRef.current) {
      timeHeaderScrollRef.current.scrollLeft = sl;
    }
    if (bottomScrollbarRef.current && bottomScrollbarRef.current.scrollLeft !== sl) {
      bottomScrollbarRef.current.scrollLeft = sl;
    }
    isSyncingScrollRef.current = false;
  }, []);

  // Reverse sync: when the user drags the sticky-bottom scrollbar, push
  // the chart area to match (which will also update the time header via
  // handleChartScroll on the next tick — but we update it inline to
  // avoid a one-frame flash where the header and chart disagree).
  const handleBottomScrollbarScroll = useCallback(() => {
    if (isSyncingScrollRef.current) return;
    if (!bottomScrollbarRef.current) return;
    isSyncingScrollRef.current = true;
    const sl = bottomScrollbarRef.current.scrollLeft;
    if (chartAreaRef.current && chartAreaRef.current.scrollLeft !== sl) {
      chartAreaRef.current.scrollLeft = sl;
    }
    if (timeHeaderScrollRef.current) {
      timeHeaderScrollRef.current.scrollLeft = sl;
    }
    isSyncingScrollRef.current = false;
  }, []);


  if (loading) {
    return (
      <div style={styles.container}>
        <LoadingState message="Loading schedule…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>
      </div>
    );
  }

  if (!tasks.length) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
          No tasks with dates to display. Add start and finish dates to your tasks to see them here.
        </div>
      </div>
    );
  }

  // Split view: overflowX clip, not hidden (2026-10-24) -- hidden made this box a scroll container that never
  // scrolls, so the sticky scrollbar at the bottom stuck to it (under the last task) instead of the bottom of
  // the window. The top block doesn't stick here: against the window it would slide under the pinned header.
  const containerStyle = inSplitView
    ? { ...styles.container, maxHeight: 'none', overflowY: 'visible', overflowX: 'clip', borderRadius: 0, boxShadow: 'none' }
    : styles.container;
  return (
    <div style={containerStyle}>
      {/* Sticky top block: toolbar + legend + column/time headers */}
      <div style={inSplitView ? { ...styles.stickyTopBlock, position: 'relative' } : styles.stickyTopBlock}>
        {/* Toolbar */}
        <div style={styles.toolbar}>
          {!inSplitView && (
            <div style={styles.toolbarLeft}>
              <div>
                <span style={styles.title}>Schedule Gantt Chart</span>
                <div style={{ fontSize: '13.5px', color: 'var(--text-3)', marginTop: '2px' }}>
                  {tasks.filter(t => t.start_date && t.end_date).length} tasks with dates · Drag rows to reorder
                </div>
              </div>
            </div>
          )}
          <div style={styles.toolbarRight}>
            <button style={styles.todayBtn} onClick={scrollToToday} title="Scroll to today">
              Today
            </button>
            <TestDateControl />
            <div style={styles.zoomGroup}>
              {ZOOM_LEVELS.map((lvl, i) => (
                <button
                  key={lvl.key}
                  style={{
                    ...styles.zoomBtn,
                    ...(i === zoomLevel ? styles.zoomBtnActive : {}),
                    borderRadius: i === 0 ? '6px 0 0 6px' : i === ZOOM_LEVELS.length - 1 ? '0 6px 6px 0' : '0',
                  }}
                  onClick={() => setZoomLevel(i)}
                >
                  {lvl.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        {!inSplitView && (
          <div style={styles.legend}>
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#9ca3af' }} /><span>Not Started</span></div>
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#2563eb' }} /><span>In Progress</span></div>
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#16a34a' }} /><span>Complete</span></div>
            <div style={styles.legendItem}><div style={{ width: '16px', height: '1px', background: 'var(--accent)', opacity: 0.55 }} /><span>Today</span></div>
            <div style={styles.legendItem}>
              <svg width="20" height="10"><path d="M0,5 L14,5" stroke="#94a3b8" strokeWidth="1.5" /><path d="M10,2 L16,5 L10,8 Z" fill="#94a3b8" /></svg>
              <span>Dependency</span>
            </div>
          </div>
        )}

        {/* Dependency alerts banner */}
        {depAlerts.summary?.total > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '6px 20px',
            backgroundColor: depAlerts.summary.critical > 0 ? '#fef2f2' : '#fffbeb',
            borderBottom: `1px solid ${depAlerts.summary.critical > 0 ? '#fecaca' : '#fde68a'}`,
            fontSize: '14.5px',
            color: depAlerts.summary.critical > 0 ? '#dc2626' : '#d97706',
          }}>
            <span style={{ fontSize: '16.5px' }}>&#9888;</span>
            <span style={{ fontWeight: 600 }}>
              {depAlerts.summary.total} scheduling {depAlerts.summary.total === 1 ? 'alert' : 'alerts'}
            </span>
            {depAlerts.summary.critical > 0 && (
              <span style={{ fontSize: '13.5px', opacity: 0.85 }}>
                ({depAlerts.summary.critical} critical{depAlerts.summary.warning > 0 ? `, ${depAlerts.summary.warning} warning` : ''})
              </span>
            )}
          </div>
        )}

        {/* Column + Time Headers Row */}
        <div style={styles.stickyHeaderRow}>
        {/* Label column header */}
        {!inSplitView && (
          <div style={{ ...styles.labelHeaderCell, width: labelWidth, minWidth: labelWidth }}>
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Name</span>
          </div>
        )}
        {/* Time header (scroll-synced with chart) — redesigned per
            Schedule handoff §5: months row 24px / 11px / 600 / text-2;
            weeks row 32px mono 10.5px text-3, with the week containing
            TODAY tinted accent-soft. */}
        <div ref={timeHeaderScrollRef} style={styles.timeHeaderScroll}>
          <div style={{ width: chartWidth, height: HEADER_HEIGHT, position: 'relative' }}>
            {/* Primary row (months) */}
            <div style={styles.timeHeaderPrimary}>
              {timeHeaders.primary.map((h, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute', left: h.left, width: h.width, height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRight: '1px solid var(--border-subtle)',
                    fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)',
                    letterSpacing: '-0.005em',
                  }}
                >
                  {h.label}
                </div>
              ))}
            </div>
            {/* Secondary row (weeks / days). When zoom = week, the cell
                whose [left, left+width) range contains todayOffset gets
                a tinted background + bold accent-text. */}
            <div style={styles.timeHeaderSecondary}>
              {timeHeaders.secondary.map((h, i) => {
                const isCurrent =
                  todayOffset !== null &&
                  todayOffset >= h.left &&
                  todayOffset < h.left + h.width;
                return (
                  <div
                    key={i}
                    className="mono"
                    style={{
                      position: 'absolute', left: h.left, width: h.width, height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRight: '1px solid var(--border-subtle)',
                      fontSize: 12,
                      color: isCurrent ? 'var(--accent-text)' : 'var(--text-3)',
                      fontWeight: isCurrent ? 600 : 500,
                      background: isCurrent
                        ? 'var(--accent-soft)'
                        : h.isWeekend
                        ? 'var(--bg-surface-2)'
                        : 'transparent',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {h.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Chart Area (labels + bars side by side) */}
      <div style={styles.chartWrapper}>
        {/* Left labels */}
        {!inSplitView && (
        <div ref={labelColumnRef} style={{ ...styles.labelColumn, width: labelWidth, minWidth: labelWidth }}>
          {/* Resize handle */}
          <div
            onMouseDown={handleResizeStart}
            style={{
              position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px',
              cursor: 'col-resize', zIndex: 25, backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#93c5fd'}
            onMouseLeave={(e) => { if (!resizingRef.current) e.currentTarget.style.backgroundColor = 'transparent'; }}
          />
          {/* Rows */}
          {visibleRows.map((row, idx) => {
            if (row.type === 'section') {
              const sectionTasks = groupedTasks[row.section];
              const taskCount = sectionTasks.length;
              const avgProgress = taskCount > 0 ? Math.round(sectionTasks.reduce((sum, t) => sum + (t.progress || 0), 0) / taskCount) : 0;
              const isExpanded = expandedSections[row.section] !== false;
              return (
                <div key={`sec-${row.section}`} style={styles.sectionLabel} onClick={() => toggleSection(row.section)}>
                  <span style={styles.sectionArrow}>{isExpanded ? '▾' : '▸'}</span>
                  <span style={styles.sectionText}>{row.section}</span>
                  <span style={styles.sectionCount}>{taskCount}</span>
                  {/* Mini progress bar */}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '48px', height: '4px', backgroundColor: 'var(--border-subtle)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${avgProgress}%`, height: '100%', backgroundColor: avgProgress >= 100 ? '#059669' : '#2563eb', borderRadius: '2px' }} />
                    </div>
                    <span style={{ fontSize: '12.5px', color: 'var(--text-3)', fontWeight: 500, minWidth: '28px' }}>{avgProgress}%</span>
                  </div>
                </div>
              );
            }
            if (row.task && row.task._addFor) return <div key={`addl-${row.task._addFor}`} style={{ height: ROW_HEIGHT, borderBottom: '1px solid var(--border-subtle)' }} />; // the list's add row
            const task = row.task;
            const sc = getProgressColors(task);
            const isDragOver = dragOverTask === task.id;
            return (
              <div
                key={`task-${task.id}`}
                draggable
                onDragStart={(e) => handleDragStart(e, task, row.section)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, task)}
                onDrop={(e) => handleDrop(e, task, row.section)}
                onDoubleClick={() => onTaskOpen && onTaskOpen(task.id)}
                style={{
                  ...styles.taskLabel,
                  backgroundColor: draggedTask?.id === task.id ? 'var(--accent-soft)' : idx % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-surface-2)',
                  borderTop: isDragOver ? '2px solid #3b82f6' : '1px solid transparent',
                  cursor: 'grab',
                }}
              >
                {/* Drag grip */}
                <span style={styles.dragGrip}>⠿</span>
                {task.code && <span style={styles.taskCode}>{task.code}</span>}
                <span style={styles.taskName} title={task.name}>{task.name}</span>
                {!!task.is_milestone && <span style={{ fontSize: '11.5px', color: milestoneColor(task, '#7c3aed') }}>&#9670;</span>}
                {flaggedTaskSet.has(task.id) && (
                  <span
                    title={alertsByTaskId[task.id]?.map(a => a.message).join('\n') || 'Scheduling alert'}
                    style={{
                      marginLeft: '4px',
                      fontSize: '14.5px',
                      cursor: 'help',
                      color: alertsByTaskId[task.id]?.some(a => a.severity === 'critical') ? '#dc2626' : '#d97706',
                    }}
                  >&#9888;</span>
                )}
              </div>
            );
          })}
        </div>
        )}

        {/* Right side: scrollable chart + sticky-bottom horizontal
            scrollbar stacked vertically. New wrapper needed because
            chartWrapper itself is display:flex (row) and the sticky
            scrollbar must sit below the chart, not next to it. */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {/* Scrollable chart. gantt-h-scroll-hidden hides chartScroll's
            native horizontal scrollbar (which would otherwise live way
            below the visible viewport when many rows are present);
            horizontal scrolling still works via wheel / trackpad / the
            sticky scrollbar below. */}
        <div ref={chartAreaRef} className="gantt-h-scroll-hidden" style={styles.chartScroll} onScroll={handleChartScroll}>
          <div style={{ width: chartWidth, minHeight: visibleRows.length * ROW_HEIGHT, position: 'relative' }}>

            {/* Vertical grid lines at every week/day boundary, per
                Schedule handoff §5: "1px --border-subtle at 60% opacity
                at every week boundary". Was using legacy t.bgAlt which
                is near-invisible against the body's slate-blue bg —
                grid was effectively absent. Token-based now.
                In day-view, weekend cells also get a subtle bg-surface-2
                wash so M-F vs Sat/Sun is glanceable. */}
            {timeHeaders.secondary.map((h, i) => (
              <React.Fragment key={`gl-${i}`}>
                {h.isWeekend && (
                  <div style={{
                    position: 'absolute', left: h.left, top: 0, bottom: 0,
                    width: h.width, background: 'var(--bg-surface-2)',
                    opacity: 0.5, zIndex: 0, pointerEvents: 'none',
                  }} />
                )}
                <div style={{
                  position: 'absolute', left: h.left, top: 0, bottom: 0,
                  width: 1, background: 'var(--border-subtle)',
                  opacity: 0.6, zIndex: 0, pointerEvents: 'none',
                }} />
              </React.Fragment>
            ))}
            {/* Month boundaries get a slightly stronger line so the
                eye finds the start of each month at a glance. */}
            {timeHeaders.primary.map((h, i) => (
              i > 0 && (
                <div key={`gl-m-${i}`} style={{
                  position: 'absolute', left: h.left, top: 0, bottom: 0,
                  width: 1, background: 'var(--border-default)',
                  opacity: 0.7, zIndex: 0, pointerEvents: 'none',
                }} />
              )
            ))}

            {/* Today indicator — sage at 55% opacity (was bright red).
                Per Schedule handoff: "The 'Today' line should never be red." */}
            {todayOffset !== null && (
              <>
                <div style={{
                  position: 'absolute', left: todayOffset, top: 0, bottom: 0,
                  width: '1px', background: 'var(--accent)', opacity: 0.55,
                  zIndex: 10, pointerEvents: 'none',
                }} />
                <div
                  className="mono"
                  style={{
                    position: 'absolute', left: todayOffset - 22, top: 2,
                    background: 'var(--accent)', color: '#fff',
                    fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
                    padding: '1px 6px', borderRadius: '3px', zIndex: 11,
                    pointerEvents: 'none', whiteSpace: 'nowrap',
                  }}
                >
                  TODAY
                </div>
              </>
            )}

            {/* Rows background + bars */}
            {visibleRows.map((row, idx) => {
              const yTop = idx * ROW_HEIGHT;

              if (row.type === 'section') {
                // Section roll-up — a thin summary bar with end-cap
                // "bookend" rectangles spanning earliest start →
                // latest finish of this section's tasks. Per handoff
                // §6 right gantt panel.
                const sectionTasks = (groupedTasks[row.section] || []).filter(t => t.start_date && t.end_date);
                let rollup = null;
                if (sectionTasks.length > 0) {
                  let minStart = sectionTasks[0].start_date;
                  let maxEnd = sectionTasks[0].end_date;
                  sectionTasks.forEach(st => {
                    if (st.start_date < minStart) minStart = st.start_date;
                    if (st.end_date > maxEnd) maxEnd = st.end_date;
                  });
                  const rollX = getBarX(minStart);
                  const rollW = Math.max(getBarWidth(minStart, maxEnd), 6);
                  // Section roll-up bar now communicates STATUS, not
                  // identity. Grey by default; green when every task in
                  // the section is 100% complete. Same rule applies to
                  // the section dot on the left task panel (TasksTab).
                  const allComplete = (groupedTasks[row.section] || []).length > 0
                    && (groupedTasks[row.section] || []).every(t => (parseFloat(t.percent_complete ?? t.progress) || 0) >= 100);
                  const sColor = allComplete ? 'var(--success)' : 'var(--text-3)';
                  // Bar 6px tall + caps 14px tall, both centered in the
                  // 36px row. Caps render after the bar so they sit on
                  // top at the ends.
                  // The section's % (its tasks by working days, lib/rollup.js) fills its bar in the theme's green, as its line in
                  // the list shows it (Duncan 2026-10-30).
                  const secPct = rollUp(groupedTasks[row.section] || [], (sharedSchedule && sharedSchedule.workWeek) || '5').pct || 0;
                  rollup = (
                    <>
                      <div style={{
                        position: 'absolute',
                        left: rollX, top: yTop + (ROW_HEIGHT - 6) / 2,
                        width: rollW, height: 6,
                        background: `color-mix(in oklch, ${sColor} 35%, transparent)`,
                        borderRadius: 99,
                        zIndex: 1,
                      }} />
                      <div title={`${row.section}: ${secPct}% (its tasks, counted by working days)`} style={{
                        position: 'absolute', left: rollX, top: yTop + (ROW_HEIGHT - 6) / 2,
                        width: (rollW * Math.min(secPct, 100)) / 100, height: 6,
                        background: secPct >= 100 ? 'var(--success)' : 'var(--accent)', borderRadius: 99, zIndex: 2,
                      }} />
                      <div style={{
                        position: 'absolute',
                        left: rollX - 1, top: yTop + (ROW_HEIGHT - 14) / 2,
                        width: 2, height: 14,
                        background: sColor, borderRadius: 1, zIndex: 2,
                      }} />
                      <div style={{
                        position: 'absolute',
                        left: rollX + rollW - 1, top: yTop + (ROW_HEIGHT - 14) / 2,
                        width: 2, height: 14,
                        background: sColor, borderRadius: 1, zIndex: 2,
                      }} />
                    </>
                  );
                }
                return (
                  <React.Fragment key={`sec-bg-${row.section}`}>
                    {/* Section background — token-based now */}
                    <div style={{
                      position: 'absolute', left: 0, top: yTop, width: '100%', height: ROW_HEIGHT,
                      // tinted as the section's line in the list (Duncan 2026-10-30: "a little bit of color to the header line item")
                      background: 'color-mix(in oklch, var(--accent) 11%, var(--bg-surface))',
                      borderTop: '1px solid var(--border-subtle)',
                      borderBottom: '1px solid color-mix(in oklch, var(--accent) 25%, var(--border-subtle))',
                    }} />
                    {rollup}
                  </React.Fragment>
                );
              }

              if (row.task && row.task._addFor) return ( // the list's "+ Add a subtask" row
                <div key={`addsub-${row.task._addFor}`} style={{ position: 'absolute', left: 0, top: yTop, width: '100%', height: ROW_HEIGHT, borderBottom: '1px solid var(--border-subtle)', backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-surface-2)' }} />
              );
              const task = row.task;
              if (!task.start_date || !task.end_date) return null;

              const barX = getBarX(task.start_date);
              const barW = getBarWidth(task.start_date, task.end_date);
              // Spec §6 Variant B split-panel: 22px tall bar in a 40px
              // row → top/bottom 9px.
              const barY = yTop + 9;
              const barH = ROW_HEIGHT - 18;
              const hasDepAlert = flaggedTaskSet.has(task.id);
              const isCriticalAlert = hasDepAlert && alertsByTaskId[task.id]?.some(a => a.severity === 'critical');
              // A sub's date move waiting on the PM (2026-10-24): a dashed amber bar at the sub's dates and a flag
              // that opens the move in the banner above; Summit's own bar stays where it is.
              const subMove = sharedSchedule?.subMoves?.[task.id];
              // A sub's progress waiting on the PM (2026-10-28, Duncan: "a visual indicator by the Gantt chart task line
              // itself"): stripes from the schedule's % to the sub's (a tick when the sub's is lower) and an amber tag
              // past the bar that opens the task at it (Task Properties: its dates, % and the sub's note).
              const subProg = sharedSchedule?.subProgress?.[task.id];
              const progress = task.progress || 0;
              const progressW = (barW * progress) / 100;
              // Type color drives bar bg / border / progress fill. Dep-alert
              // tasks override with --danger so the alert is unmissable.
              const typeColor = getTaskTypeColor(task);
              const accentColor = hasDepAlert ? 'var(--danger)' : typeColor;
              const fillColor = progress >= 100 ? 'var(--success)' : accentColor;
              const borderMix = progress === 0 ? 22 : 30;
              // A task with subtasks is a summary bracket over them, filled to its % (from its subtasks, server/subtasks.js).
              if (task._kids) {
                const w = Math.max(barW, 6);
                const shownPct = Math.round(progress);
                const ink = shownPct >= 100 ? 'var(--success)' : 'var(--text-2)';
                return (
                  <div key={`bar-${task.id}`}>
                    <div style={{ position: 'absolute', left: 0, top: yTop, width: '100%', height: ROW_HEIGHT, backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)' }} />
                    <div title={`${task.name}: ${shownPct}% (from its subtasks)`} style={{ position: 'absolute', left: barX, top: yTop + (ROW_HEIGHT - 8) / 2, width: w, height: 8, background: 'color-mix(in oklch, var(--text-3) 28%, transparent)', zIndex: 4 }}>
                      <div style={{ width: `${Math.min(shownPct, 100)}%`, height: '100%', background: ink }} />
                    </div>
                    <div style={{ position: 'absolute', left: barX, top: yTop + (ROW_HEIGHT - 18) / 2, width: 3, height: 18, background: ink, zIndex: 5 }} />
                    <div style={{ position: 'absolute', left: barX + w - 3, top: yTop + (ROW_HEIGHT - 18) / 2, width: 3, height: 18, background: ink, zIndex: 5 }} />
                    <div style={{ position: 'absolute', left: barX + w + 8, top: yTop + (ROW_HEIGHT - 16) / 2, fontSize: 12, fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{shownPct}%</div>
                  </div>
                );
              }

              return (
                <div key={`bar-${task.id}`}>
                  {/* Row background */}
                  <div style={{
                    position: 'absolute', left: 0, top: yTop, width: '100%', height: ROW_HEIGHT,
                    backgroundColor: hasDepAlert
                      ? (isCriticalAlert ? 'rgba(220, 38, 38, 0.06)' : 'rgba(217, 119, 6, 0.06)')
                      : subMove || subProg ? 'rgba(217, 119, 6, 0.06)'
                      : (idx % 2 === 0 ? 'transparent' : 'var(--bg-surface-2)'),
                    borderBottom: `1px solid var(--border-subtle)`,
                  }} />

                  {subMove && (() => {
                    const gx = getBarX(subMove.start);
                    const gw = getBarWidth(subMove.start, subMove.finish);
                    const said = `${subMove.sub || 'The sub'} moved it to ${fmtRange(subMove.start, subMove.finish)}. Billing follows Summit's dates until you accept.`;
                    return (
                      <>
                        <div title={said} style={{
                          position: 'absolute', left: gx, top: barY - 3, width: gw, height: barH + 6,
                          border: '1.5px dashed var(--warning)', borderRadius: 7, boxSizing: 'border-box',
                          zIndex: 6, pointerEvents: 'none',
                        }} />
                        <button type="button" onClick={() => sharedSchedule.openMove(subMove.id)} title={said} aria-label={said}
                          style={{
                            position: 'absolute', left: Math.max(gx + gw, barX + barW) + 4, top: barY + (barH - 20) / 2,
                            width: 20, height: 20, padding: 0, borderRadius: 6, cursor: 'pointer', zIndex: 8,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--warning)', background: 'var(--warning-soft)',
                            border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)',
                          }}>
                          <Icon name="flag" size={12} />
                        </button>
                      </>
                    );
                  })()}

                  {subProg && (() => {
                    // past the bar, and past a date move's flag when there's one
                    const left = subMove
                      ? Math.max(getBarX(subMove.start) + getBarWidth(subMove.start, subMove.finish), barX + barW) + 28
                      : barX + barW + 4;
                    const said = saidOf(subProg);
                    return (
                      <button type="button" onClick={() => sharedSchedule.openTaskProgress(task.id)} title={said} aria-label={said}
                        style={{
                          position: 'absolute', left, top: barY + (barH - 20) / 2, height: 20, padding: '0 6px', borderRadius: 6,
                          cursor: 'pointer', zIndex: 8, display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
                          color: 'var(--warning)', background: 'var(--warning-soft)',
                          border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)',
                        }}>
                        <Icon name={subProg.sub_pct < progress ? 'trending-down' : 'trending-up'} size={12} />{subProg.sub_pct}%
                      </button>
                    );
                  })()}

                  {task.is_milestone ? (
                    // Milestone diamond — filled type color, 12% mix outer ring.
                    // 11×11 box rotated 45° centered on barX.
                    <div
                      style={{
                        position: 'absolute',
                        left: barX - 5.5, top: barY + (barH - 11) / 2,
                        width: 11, height: 11,
                        background: accentColor,
                        transform: 'rotate(45deg)',
                        boxShadow: `0 0 0 3px color-mix(in oklch, ${accentColor} 12%, transparent)`,
                        cursor: 'pointer', zIndex: 5,
                      }}
                      onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, task })}
                      onMouseLeave={() => setTooltip(null)}
                      onDoubleClick={() => onTaskOpen && onTaskOpen(task.id)}
                    />
                  ) : (
                    // Normal bar — 12% mix bg, 30% mix border (22% when empty),
                    // 0.85 opacity progress fill in type color (or --success
                    // at 100%). Per Schedule handoff §6 Variant B.
                    <>
                      <div
                        style={{
                          position: 'absolute', left: barX, top: barY, width: barW, height: barH,
                          background: `color-mix(in oklch, ${accentColor} 12%, transparent)`,
                          border: `1px solid color-mix(in oklch, ${accentColor} ${borderMix}%, transparent)`,
                          borderRadius: 5, overflow: 'hidden',
                          cursor: 'pointer', zIndex: 5,
                        }}
                        onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, task })}
                        onMouseLeave={() => setTooltip(null)}
                        onDoubleClick={() => onTaskOpen && onTaskOpen(task.id)}
                      >
                        {/* Progress fill — stronger accent only over the
                            completed portion (the "stronger accent for
                            progress portion only" rule). */}
                        <div style={{
                          position: 'absolute', left: 0, top: 0, height: '100%',
                          width: progressW, background: fillColor, opacity: 0.85,
                          borderRadius: progress >= 100 ? '4px' : '4px 0 0 4px',
                          transition: 'width 0.3s ease',
                        }} />
                        {/* The sub's progress waiting on the PM: stripes up to it, or a tick where it is when it's lower */}
                        {subProg && subProg.sub_pct > progress && (
                          <div style={{
                            position: 'absolute', left: progressW, top: 0, height: '100%', width: (barW * (subProg.sub_pct - progress)) / 100,
                            background: 'repeating-linear-gradient(45deg, color-mix(in oklch, var(--warning) 55%, transparent) 0 4px, transparent 4px 8px)',
                            borderLeft: '1.5px solid var(--warning)', boxSizing: 'border-box', pointerEvents: 'none',
                          }} />
                        )}
                        {subProg && subProg.sub_pct < progress && (
                          <div style={{
                            position: 'absolute', left: (barW * subProg.sub_pct) / 100 - 1, top: 0, bottom: 0, width: 2,
                            background: 'var(--warning)', pointerEvents: 'none',
                          }} />
                        )}
                        {/* Bar labels — task name flush left, % flush
                            right, both inside the bar. Per spec §6:
                            "Bar label shows the task name (not %) when
                            it fits, with the % readout flushed right".
                            Hidden when bar is too narrow to fit; tiny
                            bars still show % to the left of the bar
                            via the fallback outside this block. */}
                        {barW >= 80 && (
                          <>
                            <span
                              style={{
                                position: 'absolute', left: 7, top: 0, bottom: 0,
                                right: 44, // reserve space for the % readout
                                display: 'inline-flex', alignItems: 'center',
                                fontSize: 12, fontWeight: 500,
                                color: 'var(--text-1)', zIndex: 7,
                                whiteSpace: 'nowrap', overflow: 'hidden',
                                textOverflow: 'ellipsis', pointerEvents: 'none',
                              }}
                            >
                              {task.task_name || task.name}
                            </span>
                            <span
                              className="mono"
                              style={{
                                position: 'absolute', right: 7, top: 0, bottom: 0,
                                display: 'inline-flex', alignItems: 'center',
                                fontSize: 12, fontWeight: 500,
                                // White only where the fill reaches under the readout -- at 41-80% it sat white on
                                // the pale unfilled end (2026-10-23).
                                color: progressW >= barW - 40 ? '#fff' : 'var(--text-2)',
                                zIndex: 7, pointerEvents: 'none',
                              }}
                            >
                              {progress}%
                            </span>
                          </>
                        )}
                      </div>
                      {/* For bars too narrow to host inline labels,
                          fall back to a % readout flush-left of the
                          bar (legacy behavior). */}
                      {barW < 80 && (
                        <span
                          className="mono"
                          style={{
                            position: 'absolute', left: barX - 4, top: barY,
                            transform: 'translateX(-100%)',
                            fontSize: '12px', fontWeight: 500, color: 'var(--text-2)',
                            lineHeight: `${barH}px`, whiteSpace: 'nowrap', zIndex: 6,
                          }}
                        >
                          {progress}%
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Dependency arrows SVG */}
            <svg
              ref={svgRef}
              style={{ position: 'absolute', top: 0, left: 0, width: chartWidth, height: visibleRows.length * ROW_HEIGHT, pointerEvents: 'none', zIndex: 8 }}
            >
              <defs>
                <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#94a3b8" />
                </marker>
                <marker id="arrowhead-red" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#dc2626" />
                </marker>
              </defs>
              {arrows.map((a, i) => {
                const dx = a.endX - a.startX;
                const dy = a.endY - a.startY;
                const absDy = Math.abs(dy);
                let path;

                if (Math.abs(dx) < 20) {
                  const offsetX = -20;
                  path = `M${a.startX},${a.startY} C${a.startX + offsetX},${a.startY + dy * 0.3} ${a.endX + offsetX},${a.endY - dy * 0.3} ${a.endX},${a.endY}`;
                } else if (dx > 20) {
                  const cpOffset = Math.min(Math.abs(dx) * 0.4, 60);
                  path = `M${a.startX},${a.startY} C${a.startX + cpOffset},${a.startY} ${a.endX - cpOffset},${a.endY} ${a.endX},${a.endY}`;
                } else {
                  const dropX = Math.min(a.startX, a.endX) - 25;
                  const belowY = Math.max(a.startY, a.endY) + ROW_HEIGHT * 0.4;
                  path = `M${a.startX},${a.startY} Q${a.startX},${belowY} ${dropX},${belowY} Q${dropX},${a.endY} ${a.endX},${a.endY}`;
                }

                // Check if this specific dependency link is violated
                const isViolated = violatedDepPairs.has(`${a.taskId}-${a.predId}`);

                return (
                  <path
                    key={i}
                    d={path}
                    fill="none"
                    stroke={isViolated ? '#dc2626' : '#94a3b8'}
                    strokeWidth={isViolated ? 2.5 : 1.5}
                    strokeDasharray={isViolated ? '0' : '6,3'}
                    markerEnd={isViolated ? 'url(#arrowhead-red)' : 'url(#arrowhead)'}
                    opacity={isViolated ? 0.9 : 0.6}
                  />
                );
              })}
            </svg>
          </div>
        </div>
        {/* Sticky-bottom horizontal scrollbar. position: sticky pins it
            to the bottom edge of the nearest scrolling ancestor (the
            outer Gantt container), so the user always has a visible
            scroll control regardless of how many task rows are below
            the fold. Inner ghost div matches chartWidth so the
            scrollbar accurately represents the chart's full pan range. */}
        <div
          ref={bottomScrollbarRef}
          onScroll={handleBottomScrollbarScroll}
          style={{
            position: 'sticky',
            bottom: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            height: 14,
            backgroundColor: 'var(--bg-surface)',
            borderTop: `1px solid var(--border-subtle)`,
            zIndex: 15,
          }}
        >
          <div style={{ width: chartWidth, height: 1 }} />
        </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 10,
          backgroundColor: 'var(--text-1)', color: 'var(--bg-surface)', padding: '10px 14px',
          borderRadius: '8px', fontSize: '13.5px', zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'none',
          maxWidth: '280px', lineHeight: '1.5',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{tooltip.task.name}</div>
          {tooltip.task.code && <div style={{ color: 'var(--text-3)', marginBottom: '4px' }}>{tooltip.task.code}</div>}
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
            <div>
              <span style={{ color: 'var(--text-3)' }}>Start: </span>
              <span>{formatDate(tooltip.task.start_date)}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-3)' }}>End: </span>
              <span>{formatDate(tooltip.task.end_date)}</span>
            </div>
          </div>
          <div style={{ marginTop: '4px' }}>
            <span style={{ color: 'var(--text-3)' }}>Progress: </span>
            <span style={{ fontWeight: 600 }}>{tooltip.task.progress || 0}%</span>
          </div>
          {tooltip.task.duration > 0 && (
            <div style={{ marginTop: '2px' }}>
              <span style={{ color: 'var(--text-3)' }}>Duration: </span>
              <span>{tooltip.task.duration} days</span>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export const GANTT_ZOOM_STYLES = {
  // Token-based polish — was red/pink (#ef4444 on #fef2f2). The action
  // is "scroll to today", not "warning today", so the calmer ghost
  // style sits better next to the sage Today line in the Gantt.
  todayBtn: {
    padding: '5px 12px',
    fontSize: 13.5,
    fontWeight: 500,
    color: 'var(--text-2)',
    background: 'var(--bg-surface-2)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 6,
    cursor: 'pointer',
  },
  zoomGroup: {
    display: 'inline-flex',
    background: 'var(--bg-surface-2)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 7,
    padding: 2,
    gap: 2,
  },
  zoomBtn: {
    padding: '4px 10px',
    fontSize: 13.5,
    fontWeight: 500,
    color: 'var(--text-3)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 5,
    transition: 'background .12s, color .12s',
  },
  zoomBtnActive: {
    background: 'var(--bg-surface)',
    color: 'var(--text-1)',
    boxShadow: 'var(--shadow-xs)',
  },
};

const getStyles = (t) => ({
  container: {
    backgroundColor: 'var(--bg-surface)',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    maxWidth: '100%',
    width: '100%',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  loadingWrap: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
    padding: '60px 0',
  },
  spinner: {
    width: '20px', height: '20px', border: `3px solid var(--border-subtle)`, borderTopColor: '#2563eb',
    borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
  stickyTopBlock: {
    position: 'sticky', top: 0, zIndex: 30, backgroundColor: 'var(--bg-surface)',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    height: '60px', boxSizing: 'border-box',
    padding: '0 20px', borderBottom: `1px solid var(--border-subtle)`,
  },
  toolbarLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  toolbarRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { fontSize: '17.5px', fontWeight: 700, color: 'var(--text-1)' },
  taskCount: {
    fontSize: '13.5px', fontWeight: 500, color: 'var(--text-3)',
    backgroundColor: 'var(--bg-surface-2)', padding: '2px 8px', borderRadius: '10px',
  },
  ...GANTT_ZOOM_STYLES,
  chartWrapper: {
    display: 'flex', position: 'relative',
    width: '100%', maxWidth: '100%',
  },
  stickyHeaderRow: {
    display: 'flex',
    // Tokens override the legacy slate-blue useTheme palette so the
    // timeline header matches the redesigned chrome above it.
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
  },
  labelHeaderCell: {
    display: 'flex', alignItems: 'flex-end', padding: '0 16px 8px',
    height: HEADER_HEIGHT, boxSizing: 'border-box',
    borderRight: `2px solid var(--border-subtle)`, flexShrink: 0,
  },
  timeHeaderScroll: {
    flex: 1, overflow: 'hidden', height: HEADER_HEIGHT,
  },
  labelColumn: {
    width: LABEL_WIDTH, minWidth: LABEL_WIDTH, borderRight: `2px solid var(--border-subtle)`,
    backgroundColor: 'var(--bg-surface)', zIndex: 20, flexShrink: 0, position: 'relative',
  },
  sectionLabel: {
    display: 'flex', alignItems: 'center', height: ROW_HEIGHT, padding: '0 16px',
    backgroundColor: 'var(--bg-surface-2)', borderTop: `1px solid var(--border-subtle)`, borderBottom: `1px solid var(--border-subtle)`,
    cursor: 'pointer', userSelect: 'none',
  },
  sectionArrow: { fontSize: '13.5px', color: 'var(--text-3)', marginRight: '6px', width: '12px', flexShrink: 0 },
  sectionText: { fontSize: '14.5px', fontWeight: 600, color: t.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sectionCount: {
    marginLeft: '6px', fontSize: '12.5px', fontWeight: 500, color: 'var(--text-3)',
    backgroundColor: 'var(--border-subtle)', padding: '1px 6px', borderRadius: '8px', flexShrink: 0,
  },
  taskLabel: {
    display: 'flex', alignItems: 'center', height: ROW_HEIGHT, padding: '0 10px 0 28px',
    borderBottom: `1px solid var(--border-subtle)`, gap: '8px', overflow: 'hidden',
  },
  taskName: {
    fontSize: '14.5px', fontWeight: 400, color: t.text, flex: 1, minWidth: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  taskCode: {
    fontSize: '13.5px', color: 'var(--text-3)', fontWeight: 500, flexShrink: 0,
    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
  },
  dragGrip: {
    color: 'var(--text-3)', fontSize: '15.5px', cursor: 'grab', flexShrink: 0,
    userSelect: 'none', lineHeight: 1, marginRight: '4px',
    letterSpacing: '-1px',
  },
  chartScroll: { flex: 1, overflowX: 'auto' },
  // Months row: 24px (was 26) per spec — keeps the two rows visually
  // distinct without bloating the sticky header.
  timeHeaderPrimary: { position: 'relative', height: '24px', borderBottom: '1px solid var(--border-subtle)' },
  // Weeks row fills the remaining HEADER_HEIGHT - 24 (= 27).
  timeHeaderSecondary: { position: 'relative', height: 'calc(100% - 24px)' },
  legend: {
    display: 'flex', alignItems: 'center', gap: '20px', padding: '10px 20px',
    borderBottom: `1px solid var(--border-subtle)`, fontSize: '13.5px', color: 'var(--text-3)',
    backgroundColor: 'var(--bg-surface)',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px' },
  legendDot: { width: '10px', height: '10px', borderRadius: '3px' },
});

export default GanttTab;
